import React from "react";
import PropTypes from "prop-types";
import {NavLink} from "react-router-dom";
import {Chip, ListItemButton, ListItemText} from "@mui/material";
import StyledBadge from "../badges/StyledBadge";
import StyledTooltip from "../tooltips/StyledTooltip";
import {Can} from "../../context/AuthzProvider";
import {useSidebar} from "./Context/SidebarProvider";
import {useFeatureFlags} from "../../context/FeatureFlagProvider";
import UpgradeAdornment from "../UpgradeAdornment";
import SidebarIcon from "./SidebarIcon";
import {useSidebarItemStyles} from "./sidebarStyles";

const SidebarEntry = ({
  to,
  text,
  icon,
  hide,
  badge,
  label,
  needUpgrade,
  relatedModelName,
  inOverlay = false,
}) => {
  const {newSidebarDesign} = useFeatureFlags();
  const {currentPage, setCurrentPage, isSidebarHidden, showSidebarText} = useSidebar();
  const isCurrentPage = to.includes(currentPage);
  const shouldShowText = newSidebarDesign ? inOverlay || showSidebarText : !isSidebarHidden;
  const {classes} = useSidebarItemStyles(
    newSidebarDesign ?
      {newSidebarDesign: true, isCurrentPage, inOverlay: shouldShowText} :
      {newSidebarDesign: false, isSidebarHidden, isCurrentPage},
  );

  if (hide) return null;

  const tooltipTitle = `${text}${badge ? ` (${badge})` : ""}`;

  const content = (
    <ListItemButton
      component={!needUpgrade ? NavLink : undefined}
      to={to}
      id={`sidebar-${text}`}
      onClick={() => setCurrentPage(to)}
      className={classes.item}
      disableRipple
    >
      <StyledBadge badgeContent={!needUpgrade && !shouldShowText && badge}>
        <SidebarIcon icon={icon} text={text} />
      </StyledBadge>
      <StyledBadge badgeContent={!needUpgrade && shouldShowText && badge} max={999} padParent>
        <ListItemText
          primary={
            <>
              {text}
              {label && <Chip label={label} size="small" className={classes.labelChip} />}
            </>
          }
          className={shouldShowText ? classes.overlayItemText : classes.itemText}
          primaryTypographyProps={{variant: "body2", component: "div"}}
        />
      </StyledBadge>
      {needUpgrade && <UpgradeAdornment fontSize="small" />}
    </ListItemButton>
  );

  const wrappedContent = newSidebarDesign ? (
    inOverlay || showSidebarText ? (
      content
    ) : (
      <StyledTooltip title={tooltipTitle} placement="right">
        {content}
      </StyledTooltip>
    )
  ) : (
    content
  );

  return (
    <Can I="update" a={relatedModelName}>
      {wrappedContent}
    </Can>
  );
};

SidebarEntry.propTypes = {
  to: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  hide: PropTypes.bool,
  badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  needUpgrade: PropTypes.bool,
  relatedModelName: PropTypes.string,
  inOverlay: PropTypes.bool,
};

export default SidebarEntry;
